import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../services/api';
import { useUserRoles } from './useUserRoles';
import { useTenantContext } from './useTenantId';

/**
 * Hook that mirrors useTenantModules but for plugins activated for the current
 * tenant. Backed by `GET /api/v1/tenant/plugins` — a simple per-tenant listing
 * that reads `module_tenant_configs`.
 *
 * IMPORTANT: We deliberately do NOT swallow errors here. Swallowing them
 * (returning []) would look like "success with empty list" to React Query,
 * `isError` would stay false, and `isPluginActive` would return false for
 * every plugin — hiding sidebar entries and redirecting guarded routes even
 * when the plugin is enabled. Instead we let the error propagate so
 * `useQuery.isError` becomes true, which triggers the fail-open branch in
 * `isPluginActive` below.
 */
interface ActivePlugin {
  plugin_id: string;
  enabled: boolean;
  settings?: Record<string, unknown>;
}

async function fetchActivePlugins(): Promise<ActivePlugin[]> {
  const { data } = await apiClient.get<ActivePlugin[] | { plugins: ActivePlugin[] }>(
    '/tenant/plugins',
  );
  return Array.isArray(data) ? data : data?.plugins ?? [];
}

export function useActivePlugins() {
  const { isResolved, tenantId } = useTenantContext();
  const { isSuperuser, isLoading: rolesLoading } = useUserRoles();

  const enabled = isResolved && !!tenantId;

  const { data, isError, isSuccess } = useQuery({
    queryKey: ['active-plugins', tenantId],
    queryFn: fetchActivePlugins,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const enabledPlugins = (data ?? []).filter(p => p.enabled).map(p => p.plugin_id);

  // "Ready" = we either have a resolved answer from the API or an explicit
  // error. Anything else (tenant not yet resolved, roles still loading,
  // query disabled, query pending) must NOT trigger the redirect — React
  // Query v5 reports isLoading=false when enabled=false, so relying on
  // isLoading alone would redirect legitimate users during the first
  // paint before tenant context resolves.
  const pluginsReady = isSuccess || isError;
  const isPending = !pluginsReady || rolesLoading || !enabled;

  const isPluginActive = (pluginKey: string): boolean => {
    if (isSuperuser) return true;           // superadmin bypass (post-load)
    if (isPending) return true;             // fail-open until we know
    if (isError) return true;               // fail-open on API errors
    return enabledPlugins.includes(pluginKey);
  };

  return {
    enabledPlugins,
    isPluginActive,
    isLoading: isPending,
    isError,
  };
}
